import React from "react";
import { Clock, CheckCircle2, FileEdit, HelpCircle } from "lucide-react";

const TrashIcon = ({ className = "h-5 w-5" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M3 6h18" />
    <path d="M8 6V4h8v2" />
    <path d="M19 6l-1 14H6L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
  </svg>
);

// Helper function to get dynamic styles and icons based on status
const getStatusBadge = (status) => {
  const normalizedStatus = (status || "DRAFT").toUpperCase();

  switch (normalizedStatus) {
    case 'SENT':
    case 'SIGNED':
      return {
        bg: 'bg-success bg-opacity-10',
        text: 'text-success',
        border: 'border-success border-opacity-25',
        iconColor: 'text-success',
        Icon: CheckCircle2
      };
    case 'DRAFT':
      return {
        bg: 'bg-warning bg-opacity-10',
        text: 'text-warning',
        border: 'border-warning border-opacity-25',
        iconColor: 'text-warning',
        Icon: FileEdit
      };
    case 'PENDING':
    case 'PENDING_SIGNATURE':
      return {
        bg: 'bg-info bg-opacity-10',
        text: 'text-info',
        border: 'border-info border-opacity-25',
        iconColor: 'text-info',
        Icon: Clock
      };
    default:
      return {
        bg: 'bg-secondary bg-opacity-10',
        text: 'text-secondary',
        border: 'border-secondary border-opacity-25',
        iconColor: 'text-secondary',
        Icon: HelpCircle
      };
  }
};

const LetterCard = ({ letter, onView, onDelete, onEdit }) => {
  const recipient = letter.letter_recipients?.[0] || letter.LetterRecipients?.[0]; 
  
  const handleDeleteClick = () => {
    // Simple confirmation dialog
    if (window.confirm("Are you sure you want to delete this letter? This action cannot be undone.")) {
      onDelete(letter.letter_id);
    }
  };

  // Get the dynamic styles for this specific letter's status
  const badgeStyles = getStatusBadge(letter.status);
  const StatusIcon = badgeStyles.Icon;

  return (
    <div
      className="
        w-100
        rounded-3 border border-secondary
        bg-white shadow-sm
        hover-shadow transition
      "
      style={{ maxWidth: "520px" }}
    >
      {/* HEADER */}
      <div className="bg-light px-4 px-lg-5 py-4 py-lg-5 border-bottom rounded-top-3">
        <h3 className="h5 fw-semibold text-dark text-truncate mb-0">
          {letter.subject}
        </h3>
      </div>

      {/* BODY */}
      <div className="px-4 px-lg-5 py-4 py-lg-5">
        {/* DYNAMIC STATUS BADGE */}
        <div className="mb-4 d-flex align-items-center">
          <span className={`
            d-inline-flex align-items-center gap-1 px-3 py-1 rounded-pill 
            fs-6 fw-bold text-uppercase tracking-wider border
            ${badgeStyles.bg} ${badgeStyles.text} ${badgeStyles.border}
          `} style={{ fontSize: "11px", letterSpacing: "0.05em" }}>
            <StatusIcon size={14} className={badgeStyles.iconColor} />
            {(letter.status || "DRAFT").replace('_', ' ')}
          </span>
        </div>

        {/* DETAILS */}
        <div className="mt-2">
          <p className="mb-2 text-secondary" style={{ fontSize: "0.875rem" }}>
            <span className="fw-semibold text-dark">Recipient:</span>{" "}
            {recipient?.name || "N/A"}
          </p>

          <p className="mb-2 text-secondary" style={{ fontSize: "0.875rem" }}>
            <span className="fw-semibold text-dark">Email:</span>{" "}
            {recipient?.email || "N/A"}
          </p>

          <p className="mb-2 text-secondary" style={{ fontSize: "0.875rem" }}>
            <span className="fw-semibold text-dark">Created:</span>{" "}
            {new Date(letter.created_at || Date.now()).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric"
            })}
          </p>

          {/* Perfect UI for "Sent at" with Date, Time, and subtle highlight */}
          {letter.status !== "DRAFT" && (letter.sent_at || letter.updated_at) && (
            <div className="mt-2 d-inline-flex align-items-center gap-1 py-1 rounded">
              <span className="fw-bold" style={{ fontSize: "13px" }}>Sent Date:</span>
              <span className="fw-medium" style={{ fontSize: "13px" }}>
                {new Date(letter.sent_at || letter.updated_at).toLocaleString("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true
                })}
              </span>
            </div>
          )}
        </div>

        {/* ACTIONS */}
        <div className="mt-4 d-flex align-items-center gap-3">
          <button
            onClick={() => onView(letter)}
            className="
              flex-grow-1 rounded-3
              bg-light py-2
              text-sm fw-medium text-dark
              hover-bg-secondary transition
            "
            style={{ fontSize: "0.875rem" }}
          >
            View
          </button>

          <button
            onClick={() => onEdit(letter.letter_id)}
            className="
              flex-grow-1 rounded-3
              bg-info bg-opacity-25 py-2
              text-sm fw-medium text-info
              hover-bg-info hover-bg-opacity-35 transition
            "
            style={{ fontSize: "0.875rem" }}
          >
            Edit
          </button>

          {/* DELETE */}
          <button
            title="Delete"
            onClick={handleDeleteClick}
            className="
              ms-1 rounded p-2
              text-secondary
              hover-text-danger hover-bg-danger hover-bg-opacity-10
              transition border-0 bg-transparent
            "
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default LetterCard;